import { useState, type FormEvent } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { radiusForPreset, radiusPresetFor, type StoreRadiusPreset } from '@fewerbags/core';
import type { StoreDto } from '@fewerbags/schema';
import { Alert } from '../Alert';
import { Button } from '../Button';
import { api } from '../../lib/api';
import { messageForError } from '../../lib/errors';
import { storeQueryKeys } from '../../pages/stores/queryKeys';
import { RadiusField } from './RadiusField';

/**
 * Edit a store's detection radius. The store row is shared, so the API lets
 * only the household that added it (or an admin) change it; anyone else gets
 * the API's explanation when they save.
 */
export function StoreRadiusSection({ store }: { store: StoreDto }) {
  const qc = useQueryClient();
  const [preset, setPreset] = useState<StoreRadiusPreset>(radiusPresetFor(store.radiusM));
  const [custom, setCustom] = useState(String(store.radiusM));
  const [formError, setFormError] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: (radiusM: number) => api.stores.update(store.id, { radiusM }),
    onSuccess: async () => {
      await Promise.all([
        qc.invalidateQueries({ queryKey: storeQueryKeys.detail(store.id) }),
        qc.invalidateQueries({ queryKey: ['stores', 'list'] }),
      ]);
    },
  });

  const radiusM = radiusForPreset(preset, custom);

  function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setFormError(null);
    save.reset();
    if (radiusM === null) {
      setFormError('Enter a whole number of metres in range.');
      return;
    }
    save.mutate(radiusM);
  }

  return (
    <form onSubmit={onSubmit} noValidate className="flex flex-col gap-3">
      <p className="text-sm text-muted">Currently {store.radiusM} m.</p>
      <RadiusField
        idPrefix={`store-${store.id}`}
        preset={preset}
        custom={custom}
        onPresetChange={setPreset}
        onCustomChange={setCustom}
        error={formError ?? undefined}
      />
      {save.isError ? <Alert tone="error">{messageForError(save.error)}</Alert> : null}
      {save.isSuccess ? <Alert tone="success">Radius saved.</Alert> : null}
      <Button
        type="submit"
        className="w-auto self-start"
        disabled={save.isPending || radiusM === store.radiusM}
      >
        {save.isPending ? 'Saving…' : 'Save radius'}
      </Button>
    </form>
  );
}
